import { css } from '@emotion/react';
import { Alert, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { ConfigStateChip } from '../../AdminPanel/components/ConfigStateChip';
import { DetailsConfigActions } from '../../AdminPanel/DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../../AdminPanel/DataDisplay/ExitConfigBlocker';
import { ConnectionDetailsSection } from '../components/ConnectionDetailsSection';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { ArkPesFormValues } from './arkPesConfig.schema';
import { ArkPesFundsSection } from './ArkPesFundsSection';
import { useArkPesConfigFormState } from './useArkPesConfigFormState';

const sectionCss = css`
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 8px;
  padding: 1rem;
`;

export function ArkPesConfiguration() {
  const { editStatus, isConnected, isLoading, successMessage, errorMessage, methods, onSave, onReset } =
    useArkPesConfigFormState();

  const description =
    'Configure your Ark PES integration credentials. Changes apply to your entire organization immediately after saving.';

  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' p='1rem' gap='1rem' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <IntegrationDetailHeader
          appName={AppName.ArkPes}
          description={description}
          extraChips={<ConfigStateChip configStatus={editStatus} />}
        />

        <ConnectionDetailsSection isConnected={isConnected} />

        <Stack direction='row' gap='1rem' alignItems='center' justifyContent='flex-end'>
          <DetailsConfigActions
            save={{ handler: onSave, disabled: editStatus !== 'modified' }}
            reset={{ handler: onReset }}
          />
        </Stack>

        {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <Stack gap='1.5rem'>
          <ClientCredentialsSection />
          <ArkPesFundsSection />
        </Stack>
      </Stack>
    </FormProvider>
  );
}

function ClientCredentialsSection() {
  const {
    control,
    formState: { errors },
  } = useFormContext<ArkPesFormValues>();

  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>Client Credentials</Typography>
      <Controller
        name='clientId'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label='Client ID'
            fullWidth
            required
            placeholder='Enter your Ark PES Client ID'
            error={!!errors.clientId}
            helperText={errors.clientId?.message}
          />
        )}
      />
    </Stack>
  );
}
